import type { CatalogItem, SeriesDetails } from '@viva/core';
import { isVivaError, VivaError } from '@viva/core';
import { LazyImage } from '@viva/ui';
import { useWatching } from '../watch-progress';
import { imageUrl } from '../network';
import { useEffect, useRef, useState } from 'react';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatTime } from '../components/Scrubber';
import { useCatalog } from '../state/catalog-context';
import { usePlay } from '../state/playback-context';
import { t, useLanguage } from '../i18n';
import { remoteInUse, restoreFocus } from '../spatial-navigation';

/** Seasons and episodes of an Xtream series, loaded from the provider. */
export function SeriesScreen({ series, onBack }: { series: CatalogItem; onBack: () => void }) {
  useLanguage();
  const { catalog } = useCatalog();
  const play = usePlay();
  const [details, setDetails] = useState<SeriesDetails | null>(null);
  const [error, setError] = useState<VivaError | null>(null);
  const [seasonIndex, setSeasonIndex] = useState(0);
  const [attempt, setAttempt] = useState(0);
  // Where the viewer stopped in this series on this device, if anywhere.
  const watched = useWatching(series.sourceId, 'series').find((entry) => entry.series?.key === series.key);
  // Read when the episodes load, without reloading them each time progress is saved.
  const watchedKey = useRef(watched?.item.key);
  watchedKey.current = watched?.item.key;
  const resumeEpisode = watched && details ? details.seasons.flatMap((entry) => entry.episodes).find((episode) => episode.key === watched.item.key) : undefined;

  useEffect(() => {
    const controller = new AbortController();
    setDetails(null);
    setError(null);
    catalog.getSeriesDetails(series.sourceId, series.key, { signal: controller.signal }).then(
      (loaded) => {
        setDetails(loaded);
        // Open on the season of the episode watched last.
        const watchedSeason = loaded.seasons.findIndex((entry) => entry.episodes.some((episode) => episode.key === watchedKey.current));
        setSeasonIndex(Math.max(0, watchedSeason));
      },
      (cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(isVivaError(cause) ? cause : new VivaError('network', String(cause)));
      },
    );
    return () => controller.abort();
  }, [catalog, series.sourceId, series.key, attempt]);

  // One play order across seasons, so "next episode" continues into the next season.
  // The episodes arrive after the page: a remote's highlight still on "Back" moves to the first one.
  useEffect(() => {
    if (!details || !remoteInUse()) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('.series .screen__header')) active.blur();
    restoreFocus();
  }, [details]);

  const allEpisodes = details ? details.seasons.flatMap((season) => season.episodes) : [];
  const season = details?.seasons[seasonIndex];

  return (
    <section className="screen series">
      <header className="screen__header">
        <button type="button" className="button ghost" onClick={onBack}>
          ← {t('common.back')}
        </button>
      </header>
      <div className="series__hero">
        <div className="series__cover">
          <LazyImage className="poster__image" src={imageUrl(details?.cover ?? series.logo)} label={series.name} />
        </div>
        <div className="series__info">
          <h1>{details?.name || series.name}</h1>
          <p className="muted">{[details?.year, details?.genre, details?.rating ? `★ ${details.rating}` : null].filter(Boolean).join(' · ')}</p>
          {details?.plot ? <p className="series__plot">{details.plot}</p> : null}
          {resumeEpisode && watched ? (
            <button
              type="button"
              className="button primary series__resume"
              onClick={() => play({ item: resumeEpisode, series, queue: { items: allEpisodes, index: allEpisodes.indexOf(resumeEpisode) } })}
            >
              {t('series.continue', { episode: `S${resumeEpisode.data.season ?? 1} E${resumeEpisode.data.episode ?? 1}`, time: formatTime(watched.position) })}
            </button>
          ) : null}
        </div>
      </div>
      {error ? (
        <div>
          <ErrorMessage error={error} />
          <button type="button" className="button" onClick={() => setAttempt((n) => n + 1)}>
            {t('common.tryAgain')}
          </button>
        </div>
      ) : null}
      {!details && !error ? <p className="muted">{t('series.loading')}</p> : null}
      {details ? (
        <>
          <div className="segmented" role="tablist" aria-label={t('series.seasons')}>
            {details.seasons.map((entry, index) => (
              <button
                key={entry.number}
                type="button"
                role="tab"
                aria-selected={index === seasonIndex}
                className="segmented__item"
                onClick={() => setSeasonIndex(index)}
              >
                {entry.name} ({entry.episodes.length})
              </button>
            ))}
          </div>
          <ul className="episode-list">
            {season?.episodes.map((episode) => (
              <li key={episode.key}>
                <button
                  type="button"
                  className="episode"
                  data-autofocus={episode === season.episodes[0] ? '' : undefined}
                  onClick={() => play({ item: episode, queue: { items: allEpisodes, index: allEpisodes.indexOf(episode) } })}
                >
                  <span className="episode__number">{episode.data.episode}</span>
                  <span className="episode__thumb">
                    <LazyImage className="poster__image" src={imageUrl(episode.logo)} label={episode.name} />
                  </span>
                  <span className="episode__text">
                    <strong>{episode.name}</strong>
                    {episode.data.plot ? <span className="muted small episode__plot">{episode.data.plot}</span> : null}
                  </span>
                  {episode.data.durationSeconds ? <span className="muted small">{formatTime(episode.data.durationSeconds)}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
